import * as React from "react";

import { ToolbarType, UitkToolbar } from "uitk-react-toolbar";
import { UitkSecondaryButton } from "uitk-react-button";
import { UitkDialogContent, UitkFullscreenDialog } from "uitk-react-dialog";
import { UitkHeading, UitkText } from "uitk-react-text";
import { UitkCardAloha, UitkCardAlohaContentSection } from "uitk-react-cards";

import { withStores } from "stores";
import { formatTimeSincePosted } from "../DestinationUserReviewsUtils";
import { FlexClickTracker } from "components/utility/analytics/FlexClickTracker/FlexClickTracker";
import { Action, FlexTrackingInfo, sendDelayedTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";

import { UserReview } from "typings/microserviceModels/destination-user-reviews-flex-module";
import { DestinationUserReviewsDialogProps } from "../typings";
import { useLocalization } from "@shared-ui/localization-context";

const BLOCK_OF_VISIBLE_REVIEWS = 20;

const DestinationUserReviewsDialog = withStores("analytics")((props: DestinationUserReviewsDialogProps) => {
  const { nameForTracking, reviewDialogTitle, reviews, toggleAllDestinationReviews } = props;

  const [maxVisibleReviews, setMaxVisibleReviews] = React.useState(BLOCK_OF_VISIBLE_REVIEWS);

  const showMoreReviews = () => setMaxVisibleReviews(maxVisibleReviews + BLOCK_OF_VISIBLE_REVIEWS);

  const analytics = {
    id: "DestinationUserReviewsClose",
    callback: (theId: string) => {
      const flexTrackingInfo: FlexTrackingInfo = {
        moduleName: theId,
        action: Action.CLICK,
      };

      sendDelayedTrackEvent(flexTrackingInfo, props.analytics);
    },
  };

  const ButtonTracked = FlexClickTracker(UitkSecondaryButton);
  const { formatText } = useLocalization();

  const reviewsToShow = reviews.slice(0, maxVisibleReviews).map((review: UserReview, index: number) => (
    <UitkCardAloha className="all-b-margin-four" key={review.postStayReviewId}>
      <UitkCardAlohaContentSection border="bottom" data-testid={`comment-review-${index}`}>
        <div className="CommentStyleReview all-y-margin-two all-y-padding-two all-l-margin-two all-l-padding-three">
          {review.rating && (
            <div className="all-b-margin-two">
              <UitkHeading level={3} size={400}>
                {`${review.rating}/5 - ${formatText(`destinationReviews.descriptor.${review.rating}`)}`}
              </UitkHeading>
            </div>
          )}

          <UitkText size={200}>{review.review}</UitkText>

          <div className="all-t-margin-two">
            <UitkText size={200}>
              <span className="uitk-type-bold">
                {review.reviewerDisplayName || formatText("destinationReviews.verified.traveler")}
              </span>
              {`, ${formatTimeSincePosted(review.createTime)}`}
            </UitkText>
          </div>
        </div>
      </UitkCardAlohaContentSection>
    </UitkCardAloha>
  ));

  return (
    <UitkFullscreenDialog dialogShow={false} className="FullscreenReviewsContainer">
      <UitkToolbar
        type={ToolbarType.CLOSE}
        action={toggleAllDestinationReviews}
        header={reviewDialogTitle}
        iconLabel="close reviews"
        className="elevation-1"
        analytics={analytics}
      />

      <UitkDialogContent>
        {reviewsToShow}

        {reviews.length > maxVisibleReviews && (
          <div className="uitk-type-center">
            <ButtonTracked
              onClick={showMoreReviews}
              text={formatText("destinationReviews.showMore")}
              rfrr="Dialog.ShowMore"
              moduleName={nameForTracking}
              linkName="RFRR Action Event"
            />
          </div>
        )}
      </UitkDialogContent>
    </UitkFullscreenDialog>
  );
});

export default DestinationUserReviewsDialog;
